import { Check, ChevronLeft, ChevronRight, MailCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ListPagination, ScrollArea } from '@/components/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNotifications } from '@/lib/api-hooks';
import {
  type AppNotification,
  NOTIFICATION_PAGE_SIZE_OPTIONS,
  type NotificationRead,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/lib/notifications';
import { useNotificationsStore } from '@/lib/notifications-store';
import { invalidateResource } from '@/lib/resource';
import { cn } from '@/lib/utils';

const readFilters: Array<{ value: NotificationRead; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
];

/** Deep link to the entity a notification targets, if it has a page. */
function targetHref(notification: AppNotification): string | null {
  switch (notification.targetType) {
    case 'artist':
    case 'project':
    case 'proposal':
      return notification.targetSlug
        ? `/${notification.targetType}s/${notification.targetSlug}`
        : null;
    case 'member':
      return notification.targetId ? `/members/${notification.targetId}` : null;
    default:
      return null;
  }
}

export function NotificationsPage() {
  const read = useNotificationsStore((state) => state.read);
  const page = useNotificationsStore((state) => state.page);
  const pageSize = useNotificationsStore((state) => state.pageSize);
  const setRead = useNotificationsStore((state) => state.setRead);
  const setPage = useNotificationsStore((state) => state.setPage);
  const setPageSize = useNotificationsStore((state) => state.setPageSize);

  const { data, isLoading } = useNotifications({ read, page, pageSize });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  async function handleRead(id: string) {
    await markNotificationRead(id);
    invalidateResource('notifications');
  }

  async function handleReadAll() {
    await markAllNotificationsRead();
    invalidateResource('notifications');
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {readFilters.map((filter) => (
            <Button
              key={filter.value}
              size="sm"
              variant={read === filter.value ? 'default' : 'ghost'}
              onClick={() => setRead(filter.value)}
              className="h-8"
            >
              {filter.label}
            </Button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={handleReadAll}>
          <MailCheck />
          Mark all read
        </Button>
      </div>

      <ScrollArea>
        {isLoading && items.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground text-sm">Loading notifications…</p>
        ) : items.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground text-sm">No notifications.</p>
        ) : (
          <div className="flex flex-col">
            {items.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onRead={handleRead}
              />
            ))}
          </div>
        )}

        {(total > 0 || isLoading) && (
          <ListPagination>
            <p className="text-muted-foreground text-sm">
              {isLoading ? 'Loading…' : `Showing ${start}–${end} of ${total}`}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Rows per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => setPageSize(Number(value))}
                >
                  <SelectTrigger size="sm" className="w-[4.5rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTIFICATION_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isLoading}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => (
                  <Button
                    key={number}
                    variant={number === page ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage(number)}
                    disabled={isLoading}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isLoading}
                >
                  Next
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </ListPagination>
        )}
      </ScrollArea>
    </div>
  );
}

function NotificationRow({
  notification,
  onRead,
}: {
  notification: AppNotification;
  onRead: (id: string) => void;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 border-b py-3 last:border-0',
        !notification.read && 'bg-primary/5',
      )}
    >
      <span
        className={cn(
          'mt-1.5 size-2 shrink-0 rounded-full',
          notification.read ? 'bg-transparent' : 'bg-primary',
        )}
      />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm">{notification.title}</p>
        <p className="flex flex-wrap items-center gap-2 text-muted-foreground text-sm">
          {notification.actorId && notification.actorName && (
            <Link
              to={`/members/${notification.actorId}`}
              className="font-medium text-foreground hover:underline"
            >
              {notification.actorName}
            </Link>
          )}
          {targetHref(notification) ? (
            <Link
              to={targetHref(notification) as string}
              className="font-medium text-foreground hover:underline"
            >
              {notification.targetLabel ?? targetHref(notification)}
            </Link>
          ) : (
            <span>{notification.body}</span>
          )}
        </p>
        <p className="mt-0.5 text-muted-foreground text-xs">
          {notification.type.replace('.', ' · ')} · {notification.date}
        </p>
      </div>
      {!notification.read && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 shrink-0"
          onClick={() => onRead(notification.id)}
        >
          <Check />
          Mark read
        </Button>
      )}
    </div>
  );
}
